// Funnel telemetry for the legacy surface (SC-620). The vocabulary is ADR
// apps/sc/docs/adr/0004; ADR-0005 records why the legacy tree emits it. The
// emitters are form.ts, wizard-step.ts and modal-service.ts. Nothing leaves
// the browser except a beacon to the worker's /api/visitor-events, which adds
// the visitor token from the cookie.
//
// This file replaced a Google Analytics wrapper whose body had been commented
// out for years. The Astro surfaces use src/lib/funnel.ts; this copy is a
// bridge, deleted with the other three at the last template cutover.

export type SurfaceKind = 'dialog-form' | 'inline-form';

// Mirrors the api's InvalidReason enum (services/visitor-events/schema.ts).
export type InvalidReason = 'phone-format' | 'email-format' | 'required-missing' | 'pattern' | 'other';

// Step identity: an ordinal always, the authored name and rendered variant when present.
export interface StepRef {
	stepNumber: number;
	stepName: string | null;
	stepVariant: string | null;
}

// What a submit carries to POST /api/projects as `formIntent` (the api's FormIntentRef).
interface FormIntentRef extends StepRef {
	id: string;
	seq: number;
	requestId: string | null;
	surfaceKind: SurfaceKind;
	surfaceName: string;
}

// What opened a product dialog: the clicked card's product and its context
// ('top-product', 'product-card', ...). Rides on the impression only (SC-622).
export interface SurfaceOpener {
	productId?: number | string | null;
	context?: string | null;
}

type FunnelEvent =
	| ({ event: 'impression' } & SurfaceOpener)
	| { event: 'step-view', stepNumber: number, stepName: string | null, stepVariant: string | null }
	| { event: 'engaged', stepNumber: number }
	| { event: 'invalid', stepNumber: number, reason: InvalidReason }
	| { event: 'dismiss' };

const ENDPOINT = '/api/visitor-events';

// One presentation of one capture surface to one visitor. Minted at
// impression, held in memory, never persisted: a reload is a new intent.
class FormIntent {
	readonly id = crypto.randomUUID();
	readonly requestId = $meta('request-id');
	isAccepted = false;
	private seq = 0;
	private engagedSteps: { [step: number]: true } = {};

	constructor(readonly surfaceKind: SurfaceKind, readonly surfaceName: string) {}

	send(event: FunnelEvent) {
		if (event.event == 'engaged') {
			if (this.engagedSteps[event.stepNumber]) return;
			this.engagedSteps[event.stepNumber] = true;
		}
		const { id: formIntentId, requestId, surfaceKind, surfaceName } = this;
		beacon({ formIntentId, requestId, surfaceKind, surfaceName, seq: this.seq++, ...event });
	}

	// The submit's own sequence number, one past the last event beaconed.
	submitRef(step: StepRef): FormIntentRef {
		const { id, requestId, surfaceKind, surfaceName } = this;
		return { id, seq: this.seq++, requestId, surfaceKind, surfaceName, ...step };
	}
}

interface Surface {
	kind: SurfaceKind;
	name: string;
	opener: SurfaceOpener;
	intent?: FormIntent;
	// An inline surface's events before it scrolls into view, sent after its impression.
	queued: FunnelEvent[];
}

class GridstrapAnalytics {

	private surfaces = new Map<Element, Surface>();

	//
	// Funnel
	//

	// Declares a capture surface: a dialog's root, or an inline form. Its
	// form-intent opens at impression(), not here.
	surface(root: Element, kind: SurfaceKind, name: string, opener: SurfaceOpener = {}) {
		if (!this.surfaces.has(root)) this.surfaces.set(root, { kind, name, opener, queued: [] });
	}

	// A dialog opening, or an inline form scrolling into view.
	impression(root: Element) {
		const surface = this.surfaces.get(root);
		if (!surface || surface.intent) return;
		const intent = surface.intent = new FormIntent(surface.kind, surface.name);
		intent.send({ event: 'impression', ...surface.opener });
		surface.queued.forEach(event => intent.send(event));
		surface.queued = [];
	}

	stepView(from: Element, step: StepRef) {
		this.emit(from, { event: 'step-view', ...step });
	}

	engaged(from: Element, stepNumber: number) {
		this.emit(from, { event: 'engaged', stepNumber });
	}

	invalid(from: Element, stepNumber: number, reason: InvalidReason) {
		this.emit(from, { event: 'invalid', stepNumber, reason });
	}

	// The payload's `formIntent`, or null when the surface has no open intent.
	submitRef(from: Element, step: StepRef): FormIntentRef | null {
		const found = this.find(from);
		return found && found[1].intent ? found[1].intent.submitRef(step) : null;
	}

	// The server accepted a submit, so a later close is not an abandonment.
	accepted(from: Element) {
		const found = this.find(from);
		if (found && found[1].intent) found[1].intent.isAccepted = true;
	}

	// The visitor closed the dialog, or left the page with it open. Ends the
	// surface; suppressed when the intent already has an accepted submit, so a
	// dismiss always means abandonment.
	dismiss(root: Element) {
		const surface = this.surfaces.get(root);
		this.surfaces.delete(root);
		if (surface && surface.intent && !surface.intent.isAccepted) surface.intent.send({ event: 'dismiss' });
	}

	private emit(from: Element, event: FunnelEvent) {
		const found = this.find(from);
		if (!found) return;
		const [root, surface] = found;
		// Touching a control proves an inline form is on screen.
		if (!surface.intent && event.event == 'engaged') this.impression(root);
		if (surface.intent) surface.intent.send(event);
		else surface.queued.push(event);
	}

	private find(from: Element): [Element, Surface] | undefined {
		for (let node: Element | null = from; node; node = node.parentElement) {
			const surface = this.surfaces.get(node);
			if (surface) return [node, surface];
		}
	}

	//
	// Retired
	//

	// Google Analytics, Bing UET and Google Ads conversions were deleted with
	// this file's rewrite and are not coming back. These stay as no-ops for
	// the components ADR-0005 leaves untouched (form-field, form-option,
	// button, product-list filters, table of contents): per-field, per-option
	// and per-button events were considered for the funnel and declined.
	event(_ignored?: unknown) {}
	pageview(_ignored?: unknown) {}
	search(_ignored?: unknown) {}

}

const analytics = new GridstrapAnalytics();
export default analytics;

// The step variant: the names of the controls a step actually rendered,
// sorted. Demo's first step renders `platforms` or `employees` depending on
// the product, which is what keeps its two branches separable.
export function stepVariant(root: Element): string | null {
	const names: string[] = [];
	const controls = root.querySelectorAll('input[name], select[name], textarea[name]');
	for (let i = 0; i < controls.length; i++) {
		const control = controls[i] as HTMLInputElement;
		if (control.type == 'hidden' || names.indexOf(control.name) >= 0) continue;
		names.push(control.name);
	}
	return names.length > 0 ? names.sort().join(',') : null;
}

// Why a field failed, from the class its failed validation test left on it
// (validation-tests.ts, form-field-input.ts).
export function invalidReason(fieldRoot: Element | undefined): InvalidReason {
	if (!fieldRoot) return 'other';
	const has = (name: string) => fieldRoot.classList.contains(name);
	if (has('is-invalid-required')) return 'required-missing';
	if (has('is-invalid-email')) return 'email-format';
	if (has('is-invalid-regex')) return fieldRoot.querySelector('input[type="tel"]') ? 'phone-format' : 'pattern';
	return 'other';
}

// Calls onSeen once, the first time half the element is on screen (or half
// the viewport, for an element taller than two screens).
export function whenSeen(element: Element, onSeen: () => void) {
	if (typeof IntersectionObserver == 'undefined') return onSeen();
	const observer = new IntersectionObserver(entries => {
		const seen = entries.some(entry => {
			const needed = Math.min(entry.boundingClientRect.height, window.innerHeight) / 2;
			return entry.isIntersecting && entry.intersectionRect.height >= needed;
		});
		if (!seen) return;
		observer.disconnect();
		onSeen();
	}, { threshold: [0, 0.25, 0.5, 0.75, 1] });
	observer.observe(element);
}

function $meta(name: string): string | null {
	const meta = document.querySelector(`meta[name="${name}"]`);
	return (meta && meta.getAttribute('content')) || null;
}

// text/plain keeps the beacon a simple request; the worker parses the body as
// JSON whatever its type. fetch keepalive covers a browser that refuses it.
function beacon(body: object) {
	const payload = JSON.stringify(body);
	try {
		if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, payload)) return;
	} catch (e) {}
	try {
		fetch(ENDPOINT, { method: 'POST', body: payload, keepalive: true }).catch(() => {});
	} catch (e) {}
}
